import type { Translation } from './en.ts';
import { accountIt } from './account-it.ts';
import { adminIt } from './admin-it.ts';
import { gameIt } from './game-it.ts';
import { metaIt } from './meta-it.ts';
import { socialIt } from './social-it.ts';
import { onlineIt } from './online-it.ts';
import { pwaIt } from './pwa-it.ts';

/** Italian strings. Typed against the English shape, so keys must match exactly. */
export const it: Translation = {
  common: {
    appName: 'Il Colpo',
    loading: 'Caricamento',
    back: 'Indietro',
    close: 'Chiudi',
    confirm: 'Conferma',
    cancel: 'Annulla',
    on: 'Sì',
    off: 'No',
    soon: 'In arrivo',
    yes: 'Sì',
    no: 'No',
    reset: 'Ripristina',
    version: 'Versione {{version}}',
    skipToContent: 'Vai al contenuto',
    languages: { it: 'Italiano', en: 'English' },
  },
  splash: {
    tagline: 'Un ultimo colpo.',
    tapToStart: 'Tocca per iniziare',
    steps: {
      fonts: 'Compongo i caratteri',
      graphics: 'Controllo le luci',
      language: 'Leggo la stanza',
      ready: 'Pronto',
    },
    updateRequired: 'Aggiornamento necessario',
    updateBody: 'Questa versione è troppo vecchia per giocare online. Aggiorna l’app.',
  },
  firstRun: {
    title: 'Benvenuto, socio',
    subtitle: 'Scegli la lingua. Potrai cambiarla dalle Impostazioni.',
    continue: 'Continua',
  },
  home: {
    subtitle: 'Un colpo per tre, quattro o cinque. La fiducia è facoltativa.',
    menu: {
      play: 'Gioca',
      playHint: 'Solista, allenamento, stanze private',
      online: 'Online',
      onlineHint: 'Partita rapida, classificata, amici',
      profile: 'Profilo',
      profileHint: 'La tua fedina penale',
      collection: 'Collezione',
      collectionHint: 'Abiti e cianografie',
      leaderboards: 'Classifiche',
      leaderboardsHint: 'Globali, amici, stagione',
      settings: 'Impostazioni',
      settingsHint: 'Audio, grafica, comandi',
      news: 'Novità',
      newsHint: 'Eventi e aggiornamenti',
    },
    banner: {
      open: 'Gioca',
      title: 'Colpo del giorno',
      body: 'Un solo seed per tutti. In arrivo.',
    },
  },
  play: {
    title: 'Gioca',
    modes: {
      quick: {
        name: 'Partita rapida',
        desc: '3–5 giocatori online. I bot riempiono i posti vuoti.',
      },
      ranked: { name: 'Classificata', desc: '4–5 giocatori. Stagioni e leghe.' },
      private: {
        name: 'Stanza privata',
        desc: 'Condividi un codice, un link o un QR con gli amici.',
      },
      solo: { name: 'Solista', desc: 'Tu e 2–4 bot. Riuscirai a smascherare la Talpa?' },
      training: { name: 'Allenamento', desc: 'Ogni rompicapo da solo, contro il tempo.' },
      daily: { name: 'Colpo del giorno', desc: 'Stesso seed per tutti, con i bot.' },
      tutorial: { name: 'Tutorial', desc: 'Un colpo guidato, un ruolo alla volta.' },
    },
  },
  comingSoon: {
    title: 'Non ancora aperto',
    body: 'Questa parte del gioco è in costruzione. Aprirà con un prossimo aggiornamento.',
  },
  notFound: {
    title: 'Porta sbagliata',
    body: 'Dietro questa non c’è niente.',
    home: 'Torna al covo',
  },
  settings: {
    title: 'Impostazioni',
    tabs: {
      audio: 'Audio',
      graphics: 'Grafica',
      game: 'Gioco',
      accessibility: 'Accessibilità',
      controls: 'Comandi',
      language: 'Lingua',
      notifications: 'Notifiche',
      account: 'Account e privacy',
    },
    audio: {
      master: 'Volume generale',
      music: 'Musica',
      sfx: 'Effetti sonori',
      note: 'La musica e gli effetti li fa il gioco stesso, mentre gira. Non c’è niente da scaricare.',
      test: 'Prova il suono',
    },
    graphics: {
      preset: 'Livello di qualità',
      presets: {
        auto: 'Automatico',
        low: 'Basso',
        medium: 'Medio',
        high: 'Alto',
        ultra: 'Ultra',
      },
      detected: 'Rilevato per questo dispositivo: {{preset}}',
      fpsLimit: 'Limite di fotogrammi',
      batterySaver: 'Risparmio batteria',
      batterySaverHint: 'Limita a 30 fps e spegne gli effetti pesanti.',
      effects: 'Effetti',
      bloom: 'Bagliore (bloom)',
      grain: 'Grana della pellicola',
      chromaticAberration: 'Aberrazione cromatica',
      volumetricLight: 'Luce volumetrica',
      rain: 'Pioggia',
      particles: 'Particelle',
    },
    game: {
      confirmations: 'Conferma le azioni rischiose',
      confirmationsHint: 'Chiede conferma prima di abbandonare o di bruciare un Piano B.',
      hints: 'Mostra i suggerimenti',
      animationSpeed: 'Velocità delle animazioni',
      animationSpeedValue: '{{value}}×',
    },
    accessibility: {
      colorblind: 'Modalità per daltonici',
      colorblindHint: 'Aggiunge simboli e motivi a tutto ciò che usa il colore.',
      largeText: 'Testo grande',
      highContrast: 'Alto contrasto',
      reduceMotion: 'Riduci movimento',
      reduceMotionHint: 'Niente scossoni, niente lampi, animazioni più calme.',
      reduceMotionSystem: 'Segui il sistema',
      vibration: 'Vibrazione',
      leftHanded: 'Layout per mancini',
      uiScale: 'Dimensione dell’interfaccia',
    },
    controls: {
      hint: 'Scegli un’azione, poi premi il tasto che vuoi. Solo tastiera; i comandi touch sono fissi.',
      pressKey: 'Premi un tasto…',
      conflict: '«{{key}}» è già usato da {{action}}.',
      resetAll: 'Ripristina i tasti predefiniti',
      actions: {
        quickPhrase1: 'Frase rapida 1',
        quickPhrase2: 'Frase rapida 2',
        quickPhrase3: 'Frase rapida 3',
        quickPhrase4: 'Frase rapida 4',
        quickPhrase5: 'Frase rapida 5',
        quickPhrase6: 'Frase rapida 6',
        quickPhrase7: 'Frase rapida 7',
        quickPhrase8: 'Frase rapida 8',
        quickPhrase9: 'Frase rapida 9',
        ping: 'Ping',
        toggleBlueprint: 'Mostra la pianta',
        toggleChat: 'Apri la chat',
        pause: 'Pausa / menù',
      },
    },
    language: { title: 'Lingua del gioco' },
    notifications: {
      invites: 'Inviti',
      friends: 'Richieste di amicizia',
      events: 'Eventi',
      season: 'Fine stagione',
      note: 'Le notifiche push arrivano con il gioco online.',
    },
    account: {
      note: 'Gli account non sono disponibili in questa versione. Il gioco è completo anche senza.',
      open: 'Apri l’account',
      openHint: 'Profilo, salvataggio nel cloud, privacy e i tuoi dati.',
    },
    footer: { gallery: 'Galleria dei componenti' },
  },
  gallery: {
    title: 'Galleria dei componenti',
    intro: 'Ogni componente in ogni stato. Strumento di sviluppo.',
    buttons: 'Bottoni',
    controls: 'Controlli',
    feedback: 'Riscontri',
    typography: 'Tipografia',
    palette: 'Palette',
    primary: 'Primario',
    secondary: 'Secondario',
    ghost: 'Discreto',
    danger: 'Pericolo',
    disabled: 'Disabilitato',
    loadingState: 'Caricamento',
    selected: 'Selezionato',
    sampleSwitch: 'Interruttore di prova',
    sampleSlider: 'Cursore di prova',
    sampleSegmented: 'Scelta di prova',
    optionA: 'Uno',
    optionB: 'Due',
    optionC: 'Tre',
    openModal: 'Apri finestra',
    modalTitle: 'Bruciare un Piano B?',
    modalBody: 'Annullerai l’ultimo errore e il suo allarme. Ne hai solo due.',
    showToast: 'Mostra avviso',
    toastText: 'Piano approvato.',
    tooltipTarget: 'Tieni premuto o passa sopra',
    tooltipText: 'Pressione prolungata al tocco, hover o focus su PC.',
    progress: 'Avanzamento',
    alarm: 'Allarme',
    display: 'Carattere dei titoli',
    interface: 'Carattere dell’interfaccia',
    typewriter: 'Note a macchina',
    sampleText: 'Il caveau si apre a mezzanotte.',
    badge: 'Etichetta',
  },
  ...gameIt,
  ...accountIt,
  ...onlineIt,
  ...metaIt,
  ...socialIt,
  ...adminIt,
  ...pwaIt,
};
